<script setup>
// Conteúdo das estatísticas de campanha (banner de status + cards de
// fonte/filtragem/resultado/hold + drill-down de error logs), extraído do
// CampaignStatisticsModal pra ser hospedado tanto pelo modal (WhatsApp App,
// legado) quanto pela aba "Estatísticas" do ScheduleDrawer. Busca os dados
// frescos no mount — quem hospeda monta com v-if quando exibe.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import CampaignsAPI from 'dashboard/api/campaigns';
import { toast } from 'dashboard/helper/toast';
import { useStore } from 'dashboard/composables/store';
import {
  campaignStatusLabel,
  normalizeCampaignStatus,
  CAMPAIGN_STATUS,
} from 'dashboard/routes/dashboard/campaigns/helper/campaignStatus';
import VueJsonPretty from 'vue-json-pretty';
import 'vue-json-pretty/lib/styles.css';

const props = defineProps({
  campaignId: { type: Number, default: null },
  schedulingId: { type: Number, default: null },
  statistics: { type: Object, default: null },
  progressData: { type: Object, default: () => ({}) },
  holdStats: { type: Object, default: null },
  status: { type: String, default: '' },
  statusMessage: { type: String, default: '' },
});

const store = useStore();

// Mapeamento de error_type para icones Lucide
const errorTypeIcons = {
  missing_placeholder: 'i-lucide-text-cursor-input',
  missing_required_field: 'i-lucide-file-question',
  invalid_phone: 'i-lucide-phone-off',
  NOT_ON_WHATSAPP: 'i-lucide-phone-off',
  blacklisted: 'i-lucide-ban',
  duplicate_data: 'i-lucide-copy',
  duplicate_removed: 'i-lucide-copy-minus',
  invalid_format: 'i-lucide-file-x',
  service_failed: 'i-lucide-server-crash',
  timeout: 'i-lucide-clock',
  network_error: 'i-lucide-wifi-off',
  api_error: 'i-lucide-cloud-off',
  exception: 'i-lucide-bug',
  send_failure: 'i-lucide-message-circle-x',
  // Resposta ao disparo caiu na entrada padrão: o destino gravado não existe
  // mais em produção (Ai::CampaignEntryResolver#log_campaign_error).
  reply_destination_unavailable: 'i-lucide-route-off',
};

// Mapeamento de error_category para cores
const errorCategoryColors = {
  data_error: 'text-n-amber-11',
  technical_error: 'text-n-ruby-11',
};

const getErrorIcon = errorType => {
  return errorTypeIcons[errorType] || 'i-lucide-alert-triangle';
};

const getErrorColor = errorCategory => {
  return errorCategoryColors[errorCategory] || 'text-n-amber-11';
};

const errorLogs = ref([]);
const errorSummary = ref([]);
const loading = ref(false);
const currentPage = ref(1);
const totalCount = ref(0);
const selectedFilter = ref(null);
// Set de log ids com record_data expandido (toggle por linha)
const expandedLogs = ref(new Set());

const toggleRecordData = id => {
  const next = new Set(expandedLogs.value);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  expandedLogs.value = next;
};

// Counts reais do banco (carregados ao abrir o modal)
const realCounts = ref(null);

// Statistics frescas do banco. Quando o modal abre logo apos a campanha
// completar, props.statistics pode estar parcial (vindo de broadcast WS
// intermediario que so trazia items_sent). Aqui buscamos o schedule
// atualizado via REST e usamos como fonte de verdade.
const freshStatistics = ref(null);

// Counters frescos do MESMO fetch (items_sent/items_failed do banco).
// props.progressData eh um snapshot congelado no clique (event.detail) —
// sem isso o card "Enviados" ficava preso no valor do momento do clique
// mesmo com o refetch trazendo o consolidado.
const freshCounters = ref(null);

// Cadeia de fonte dos cards de resultado: banco fresco > snapshot do clique
// > statistics. `??` (nao `||`): 0 enviado/0 falha eh valor legitimo.
const realFailed = computed(() => {
  const stats = freshStatistics.value || props.statistics || {};
  const progress = props.progressData || {};
  return (
    freshCounters.value?.items_failed ??
    progress.failed ??
    stats.failed_count ??
    0
  );
});

const realSent = computed(() => {
  const stats = freshStatistics.value || props.statistics || {};
  const progress = props.progressData || {};
  return (
    freshCounters.value?.items_sent ?? progress.sent ?? stats.items_sent ?? 0
  );
});

const perPage = 20;
const totalPages = computed(() => Math.ceil(totalCount.value / perPage));

// Items agrupados em 3 secoes visualmente separadas no modal:
//   source  - resumo da fonte (total na origem)
//   filtered - records descartados antes do envio (decisao de negocio)
//   outcome  - resultado real do envio (sucesso vs falha)
const statsItems = computed(() => {
  const items = [];
  const stats = freshStatistics.value || props.statistics || {};

  if (stats.total_from_query) {
    items.push({
      group: 'source',
      icon: 'i-lucide-database',
      label: 'Fonte',
      value: stats.total_from_query,
      color: 'text-n-slate-11',
      description: 'Registros na origem',
    });
  }

  const invalidPhoneCount =
    realCounts.value?.missing_required_field ?? stats.invalid_phone_count ?? 0;
  if (invalidPhoneCount > 0) {
    items.push({
      group: 'filtered',
      icon: 'i-lucide-phone-off',
      label: 'Sem telefone',
      value: invalidPhoneCount,
      color: 'text-n-amber-11',
      clickable: true,
      filterType: 'missing_phone',
      description: 'Clique para ver',
    });
  }

  const duplicatesCount =
    realCounts.value?.duplicate_removed ?? stats.duplicates_removed ?? 0;
  if (duplicatesCount > 0) {
    items.push({
      group: 'filtered',
      icon: 'i-lucide-copy-minus',
      label: 'Duplicatas',
      value: duplicatesCount,
      color: 'text-n-amber-11',
      clickable: true,
      filterType: 'duplicates',
      description: 'Clique para ver',
    });
  }

  const blacklistCount =
    realCounts.value?.blacklisted ?? stats.blacklisted_count ?? 0;
  if (blacklistCount > 0) {
    items.push({
      group: 'filtered',
      icon: 'i-lucide-ban',
      label: 'Blacklist',
      value: blacklistCount,
      color: 'text-n-slate-10',
      clickable: true,
      filterType: 'blacklisted',
      description: 'Clique para ver',
    });
  }

  // channel_limit_cut: descartados pelo daily_limit do canal Meta antes do envio.
  // Tambem eh "filtragem" mas nao tem records individuais (corte por count).
  const channelLimitCut = stats.channel_limit_cut || 0;
  if (channelLimitCut > 0) {
    items.push({
      group: 'filtered',
      icon: 'i-lucide-scissors',
      label: 'Cortados',
      value: channelLimitCut,
      color: 'text-n-amber-11',
      description: 'Pelo limite diario',
    });
  }

  // Total que efetivamente entrou na fila de envio.
  if (stats.valid_for_sending) {
    items.push({
      group: 'outcome',
      icon: channelLimitCut > 0 ? 'i-lucide-gauge' : 'i-lucide-users',
      label: channelLimitCut > 0 ? 'Limite' : 'Validos',
      value: stats.valid_for_sending,
      color: 'text-n-blue-11',
      description: channelLimitCut > 0 ? 'Disponivel hoje' : 'Para envio',
    });
  }

  // Counter items_failed — banco fresco > snapshot do clique (realFailed).
  // Apos backend separar 'filtered' de 'data_error', esse contador reflete
  // apenas falhas reais de envio (timeout, exception, service_failed, etc).
  const failed = realFailed.value;

  items.push({
    group: 'outcome',
    icon: 'i-lucide-check-circle',
    label: 'Enviados',
    value: realSent.value,
    color: 'text-n-teal-11',
    description: 'Com sucesso',
  });

  if (failed > 0) {
    items.push({
      group: 'outcome',
      icon: 'i-lucide-x-circle',
      label: 'Falhas',
      value: failed,
      color: 'text-n-ruby-11',
      clickable: true,
      filterType: 'failures',
      description: 'Clique para ver',
    });
  }

  return items;
});

const sourceItems = computed(() =>
  statsItems.value.filter(i => i.group === 'source')
);
const filteredItems = computed(() =>
  statsItems.value.filter(i => i.group === 'filtered')
);
const outcomeItems = computed(() =>
  statsItems.value.filter(i => i.group === 'outcome')
);

// Enviados com sucesso reais — mesma cadeia dos cards (realSent): banco
// fresco > snapshot do clique. Reflete o que de fato saiu, nao o esperado.
const sentSuccessfully = realSent;

// Título dinâmico baseado no filtro
const logsTitle = computed(() => {
  if (selectedFilter.value === 'blacklisted') {
    return 'Telefones na Blacklist';
  }
  if (selectedFilter.value === 'missing_phone') {
    return 'Registros sem Telefone';
  }
  if (selectedFilter.value === 'duplicates') {
    return 'Registros Duplicados Removidos';
  }
  if (selectedFilter.value === 'failures') {
    return 'Falhas no Processamento';
  }
  return 'Logs de Erros';
});

const { t } = useI18n();

const statusDisplay = computed(() => {
  // props.status chega CRU (enum) do CampaignStatusProgress
  const configs = {
    [CAMPAIGN_STATUS.COMPLETED]: {
      icon: 'i-lucide-check-circle',
      class: 'text-n-teal-11 bg-n-teal-3',
    },
    [CAMPAIGN_STATUS.CANCELLED]: {
      icon: 'i-lucide-x-octagon',
      class: 'text-n-amber-11 bg-n-amber-3',
    },
    [CAMPAIGN_STATUS.RUNNING]: {
      icon: 'i-lucide-loader',
      class: 'text-n-blue-11 bg-n-blue-3',
    },
    [CAMPAIGN_STATUS.FAILED]: {
      icon: 'i-lucide-alert-triangle',
      class: 'text-n-ruby-11 bg-n-ruby-3',
    },
  };
  const normalized = normalizeCampaignStatus(props.status);
  const config = configs[normalized] || configs[CAMPAIGN_STATUS.COMPLETED];
  // Rótulo via i18n; status desconhecido volta cru (não esconde)
  return { ...config, label: campaignStatusLabel(props.status, t) };
});

const isFailedStatus = computed(
  () => normalizeCampaignStatus(props.status) === CAMPAIGN_STATUS.FAILED
);

// Feature HOLD: entregas seguradas por atendimento ativo no disparo.
// A secao so aparece quando ha algum contador > 0.
const holdHeld = computed(() => props.holdStats?.held || 0);
const holdReleased = computed(() => props.holdStats?.released || 0);
const holdExpired = computed(() => props.holdStats?.expired || 0);
const holdSkipped = computed(() => props.holdStats?.skipped || 0);
const hasHoldStats = computed(
  () =>
    holdHeld.value > 0 ||
    holdReleased.value > 0 ||
    holdExpired.value > 0 ||
    holdSkipped.value > 0
);
const holdItems = computed(() => [
  {
    key: 'held',
    icon: 'i-lucide-pause',
    label: t('CAMPAIGN.STATISTICS.HOLD.HELD'),
    value: holdHeld.value,
    color: 'text-n-amber-11',
    description: t('CAMPAIGN.STATISTICS.HOLD.HELD_DESC'),
  },
  {
    key: 'released',
    icon: 'i-lucide-play',
    label: t('CAMPAIGN.STATISTICS.HOLD.RELEASED'),
    value: holdReleased.value,
    color: 'text-n-teal-11',
    description: t('CAMPAIGN.STATISTICS.HOLD.RELEASED_DESC'),
  },
  {
    key: 'expired',
    icon: 'i-lucide-hourglass',
    label: t('CAMPAIGN.STATISTICS.HOLD.EXPIRED'),
    value: holdExpired.value,
    color: 'text-n-ruby-11',
    description: t('CAMPAIGN.STATISTICS.HOLD.EXPIRED_DESC'),
  },
  {
    key: 'skipped',
    icon: 'i-lucide-ban',
    label: t('CAMPAIGN.STATISTICS.HOLD.SKIPPED'),
    value: holdSkipped.value,
    color: 'text-n-slate-11',
    description: t('CAMPAIGN.STATISTICS.HOLD.SKIPPED_DESC'),
  },
]);

const fetchErrorLogs = async (filterType = null) => {
  if (!props.campaignId || !props.schedulingId) return;

  loading.value = true;
  selectedFilter.value = filterType || 'all';

  try {
    const params = {
      page: currentPage.value,
      per_page: perPage,
    };

    // Filtros de filtragem (categoria 'filtered' no backend) precisam pedir
    // explicitamente include_filtered, pois a listagem padrao mostra apenas
    // erros reais (data_error + technical_error).
    if (filterType === 'blacklisted') {
      params.error_type = 'blacklisted';
      params.include_filtered = 'true';
    } else if (filterType === 'missing_phone') {
      params.error_type = 'missing_required_field';
      params.include_filtered = 'true';
    } else if (filterType === 'duplicates') {
      params.error_type = 'duplicate_removed';
      params.include_filtered = 'true';
    }
    // Para 'failures' nao precisa de filtro extra: o default ja mostra
    // apenas data_error + technical_error (filtered fica fora).

    const response = await CampaignsAPI.listCampaignErrorLogs(
      props.campaignId,
      props.schedulingId,
      params
    );
    errorLogs.value = response.data.error_logs || [];
    errorSummary.value = response.data.error_summary || [];
    totalCount.value = response.data.pagination?.total_count || 0;
  } catch {
    toast.error(t('CAMPAIGN.STATISTICS.LOGS_FETCH_ERROR'));
  } finally {
    loading.value = false;
  }
};

const handleStatClick = item => {
  if (item.clickable) {
    currentPage.value = 1; // Reset para primeira página ao trocar filtro
    fetchErrorLogs(item.filterType);
  }
};

const clearFilter = () => {
  selectedFilter.value = null;
  errorLogs.value = [];
  errorSummary.value = [];
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value += 1;
    fetchErrorLogs(selectedFilter.value);
  }
};

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value -= 1;
    fetchErrorLogs(selectedFilter.value);
  }
};

const fetchRealCounts = async () => {
  if (!props.campaignId || !props.schedulingId) return;
  try {
    // include_filtered=true pra trazer contagem de blacklist/duplicata/sem
    // telefone tambem (sao os cards de filtragem da UI). Sem isso, error_summary
    // padrao volta so com erros reais (timeout/exception/service_failed/etc).
    const response = await CampaignsAPI.listCampaignErrorLogs(
      props.campaignId,
      props.schedulingId,
      { page: 1, per_page: 1, include_filtered: 'true' }
    );
    const summary = response.data.error_summary || [];
    const counts = {};
    summary.forEach(s => {
      counts[s.error_type] = (counts[s.error_type] || 0) + s.count;
    });
    realCounts.value = counts;
  } catch {
    realCounts.value = null;
  }
};

// Busca o schedule atualizado via REST quando o modal abre, garantindo
// que statistics reflete o estado final no banco (nao o que estava em
// memoria no Vuex/composable, que pode estar parcial).
const fetchFreshStatistics = async () => {
  if (!props.campaignId || !props.schedulingId) return;
  try {
    await store.dispatch('campaigns/get', { include_schedules: true });
    const campaign = store.state.campaigns.records.find(
      c => c.id === props.campaignId
    );
    const schedule = campaign?.campaign_schedules?.find(
      s => s.id === props.schedulingId
    );
    if (schedule) {
      freshStatistics.value = schedule.statistics || null;
      freshCounters.value = {
        items_sent: schedule.items_sent,
        items_failed: schedule.items_failed,
      };
    }
  } catch {
    freshStatistics.value = null;
    freshCounters.value = null;
  }
};

// Busca no mount — o host (modal ou aba do drawer) monta o painel com v-if
// quando ele fica visível, então mount == abertura.
onMounted(() => {
  fetchRealCounts();
  fetchFreshStatistics();
  // Se a campanha falhou, carregar error logs automaticamente
  if (normalizeCampaignStatus(props.status) === CAMPAIGN_STATUS.FAILED) {
    currentPage.value = 1;
    fetchErrorLogs('all');
  }
});
</script>

<template>
  <div class="flex flex-col">
    <!-- Status Badge e Resumo -->
    <div
      class="flex flex-col gap-2 mb-6 p-4 rounded-xl bg-n-slate-3 border border-n-slate-6"
    >
      <div class="flex items-center gap-3">
        <span
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
          :class="statusDisplay.class"
        >
          <span class="size-4" :class="[statusDisplay.icon]" />
          {{ statusDisplay.label }}
        </span>
      </div>
      <p class="text-sm text-n-slate-11">
        <span class="font-semibold text-n-slate-12">{{
          sentSuccessfully
        }}</span>
        {{ $t('CAMPAIGN.STATISTICS.MESSAGES_SENT_FROM') }}
        <span class="font-semibold text-n-slate-12">{{
          statistics?.total_from_query || progressData.total || 0
        }}</span>
        {{ $t('CAMPAIGN.STATISTICS.RECORDS_IN_DB') }}
      </p>
    </div>

    <!-- Container do drawer: cards slide out pra esquerda, logs entram da direita -->
    <div class="relative overflow-hidden min-h-[60vh]">
      <!-- Cards (resumo + grids) — escondem quando drawer aberto -->
      <div
        class="transition-all duration-200 ease-out"
        :class="
          selectedFilter
            ? '-translate-x-2 opacity-0 pointer-events-none'
            : 'translate-x-0 opacity-100'
        "
      >
        <!-- Fonte (resumo da origem) -->
        <div v-if="sourceItems.length > 0" class="mb-4">
          <h4
            class="text-xs font-semibold uppercase tracking-wider text-n-slate-10 mb-2 flex items-center gap-1.5"
          >
            <span class="size-3.5 i-lucide-database" />
            {{ $t('CAMPAIGN.STATISTICS.GROUP_SOURCE') }}
          </h4>
          <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <button
              v-for="item in sourceItems"
              :key="item.label"
              class="flex items-center gap-3 p-4 rounded-xl border transition-all text-left bg-n-slate-3 border-n-slate-6"
              :class="[
                item.clickable
                  ? 'hover:border-n-blue-8 hover:bg-n-slate-4 cursor-pointer'
                  : 'cursor-default',
              ]"
              :disabled="!item.clickable"
              @click="handleStatClick(item)"
            >
              <div
                class="flex items-center justify-center size-10 rounded-lg bg-n-slate-4"
              >
                <span class="size-5" :class="[item.icon, item.color]" />
              </div>
              <div class="flex flex-col items-start">
                <span class="text-2xl font-bold text-n-slate-12">{{
                  item.value?.toLocaleString()
                }}</span>
                <span class="text-xs text-n-slate-11">{{ item.label }}</span>
                <span
                  v-if="item.description"
                  class="text-[10px] text-n-slate-10"
                >
                  {{ item.description }}
                </span>
              </div>
            </button>
          </div>
        </div>

        <!-- Filtrados (descartados antes do envio) -->
        <div v-if="filteredItems.length > 0" class="mb-4">
          <h4
            class="text-xs font-semibold uppercase tracking-wider text-n-amber-11 mb-2 flex items-center gap-1.5"
          >
            <span class="size-3.5 i-lucide-filter" />
            {{ $t('CAMPAIGN.STATISTICS.GROUP_FILTERED') }}
          </h4>
          <p class="text-[11px] text-n-slate-10 mb-2">
            {{ $t('CAMPAIGN.STATISTICS.GROUP_FILTERED_HINT') }}
          </p>
          <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <button
              v-for="item in filteredItems"
              :key="item.label"
              class="flex items-center gap-3 p-4 rounded-xl border transition-all text-left bg-n-amber-2 border-n-amber-5"
              :class="[
                item.clickable
                  ? 'hover:border-n-amber-8 hover:bg-n-amber-3 cursor-pointer'
                  : 'cursor-default',
              ]"
              :disabled="!item.clickable"
              @click="handleStatClick(item)"
            >
              <div
                class="flex items-center justify-center size-10 rounded-lg bg-n-amber-4"
              >
                <span class="size-5" :class="[item.icon, item.color]" />
              </div>
              <div class="flex flex-col items-start">
                <span class="text-2xl font-bold text-n-slate-12">{{
                  item.value?.toLocaleString()
                }}</span>
                <span class="text-xs text-n-slate-11">{{ item.label }}</span>
                <span
                  v-if="item.description"
                  class="text-[10px] text-n-slate-10"
                >
                  {{ item.description }}
                </span>
              </div>
            </button>
          </div>
        </div>

        <!-- Outcome (resultado do envio) -->
        <div v-if="outcomeItems.length > 0" class="mb-6">
          <h4
            class="text-xs font-semibold uppercase tracking-wider text-n-blue-11 mb-2 flex items-center gap-1.5"
          >
            <span class="size-3.5 i-lucide-send" />
            {{ $t('CAMPAIGN.STATISTICS.GROUP_OUTCOME') }}
          </h4>
          <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <button
              v-for="item in outcomeItems"
              :key="item.label"
              class="flex items-center gap-3 p-4 rounded-xl border transition-all text-left bg-n-slate-3 border-n-slate-6"
              :class="[
                item.clickable
                  ? 'hover:border-n-blue-8 hover:bg-n-slate-4 cursor-pointer'
                  : 'cursor-default',
              ]"
              :disabled="!item.clickable"
              @click="handleStatClick(item)"
            >
              <div
                class="flex items-center justify-center size-10 rounded-lg bg-n-slate-4"
              >
                <span class="size-5" :class="[item.icon, item.color]" />
              </div>
              <div class="flex flex-col items-start">
                <span class="text-2xl font-bold text-n-slate-12">{{
                  item.value?.toLocaleString()
                }}</span>
                <span class="text-xs text-n-slate-11">{{ item.label }}</span>
                <span
                  v-if="item.description"
                  class="text-[10px] text-n-slate-10"
                >
                  {{ item.description }}
                </span>
              </div>
            </button>
          </div>
        </div>

        <!-- Entregas seguradas (HOLD) — so aparece quando algum contador > 0 -->
        <div v-if="hasHoldStats" class="mb-6">
          <h4
            class="text-xs font-semibold uppercase tracking-wider text-n-amber-11 mb-2 flex items-center gap-1.5"
          >
            <span class="size-3.5 i-lucide-pause-circle" />
            {{ $t('CAMPAIGN.STATISTICS.HOLD.TITLE') }}
          </h4>
          <p class="text-[11px] text-n-slate-10 mb-2">
            {{ $t('CAMPAIGN.STATISTICS.HOLD.HINT') }}
          </p>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div
              v-for="item in holdItems"
              :key="item.key"
              class="flex items-center gap-3 p-4 rounded-xl border bg-n-amber-2 border-n-amber-5"
            >
              <div
                class="flex items-center justify-center size-10 rounded-lg bg-n-amber-4"
              >
                <span class="size-5" :class="[item.icon, item.color]" />
              </div>
              <div class="flex flex-col items-start">
                <span class="text-2xl font-bold text-n-slate-12">{{
                  item.value.toLocaleString()
                }}</span>
                <span class="text-xs text-n-slate-11">{{ item.label }}</span>
                <span class="text-[10px] text-n-slate-10">
                  {{ item.description }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- /cards -->

      <!-- Drawer: logs entram suavemente da direita (fade + slide curto) -->
      <Transition
        enter-active-class="transition duration-200 ease-out"
        leave-active-class="transition duration-150 ease-in"
        enter-from-class="translate-x-2 opacity-0"
        leave-to-class="translate-x-2 opacity-0"
      >
        <div
          v-if="selectedFilter"
          class="absolute inset-0 bg-n-solid-1 flex flex-col p-4"
        >
          <div class="flex items-center justify-between mb-4 shrink-0">
            <button
              class="flex items-center gap-1.5 text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
              @click="clearFilter"
            >
              <span class="i-lucide-arrow-left size-4" />
              {{ $t('CAMPAIGN.STATISTICS.BACK_TO_SUMMARY') }}
            </button>
            <h4
              class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
            >
              <span
                class="size-4"
                :class="[
                  selectedFilter === 'blacklisted'
                    ? 'i-lucide-ban'
                    : 'i-lucide-alert-circle',
                  selectedFilter === 'blacklisted'
                    ? 'text-n-slate-10'
                    : 'text-n-ruby-11',
                ]"
              />
              {{ logsTitle }} ({{ totalCount }})
            </h4>
          </div>

          <!-- Error Summary by Type -->
          <div
            v-if="errorSummary.length > 0 && !loading"
            class="flex flex-wrap gap-2 mb-4 shrink-0"
          >
            <div
              v-for="summary in errorSummary"
              :key="`${summary.error_type}-${summary.error_category}`"
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-n-slate-4 border border-n-slate-6"
            >
              <span
                class="size-3.5"
                :class="[
                  getErrorIcon(summary.error_type),
                  getErrorColor(summary.error_category),
                ]"
              />
              <span class="text-n-slate-11">{{
                summary.error_description
              }}</span>
              <span class="font-bold text-n-slate-12">{{ summary.count }}</span>
            </div>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="flex flex-1 items-center justify-center">
            <span
              class="i-lucide-loader-2 size-6 text-n-slate-11 animate-spin"
            />
          </div>

          <!-- Error List — ocupa todo espaco disponivel -->
          <div
            v-else-if="errorLogs.length > 0"
            class="flex flex-col gap-2 flex-1 min-h-0 overflow-y-auto"
          >
            <div
              v-for="log in errorLogs"
              :key="log.id"
              class="flex flex-col gap-2 p-3 rounded-lg bg-n-slate-4 border border-n-slate-6"
            >
              <div class="flex items-start gap-3">
                <span
                  class="size-4 mt-0.5 shrink-0"
                  :class="[
                    getErrorIcon(log.error_type),
                    getErrorColor(log.error_category),
                  ]"
                />
                <div class="flex flex-col gap-1 min-w-0 flex-1">
                  <span class="text-sm font-medium text-n-slate-12">{{
                    log.error_description
                  }}</span>
                  <span class="text-xs text-n-slate-11 line-clamp-2">{{
                    log.error_message
                  }}</span>
                  <div
                    class="flex items-center flex-wrap gap-3 text-xs text-n-slate-10"
                  >
                    <span
                      v-if="log.contact_name"
                      class="flex items-center gap-1"
                    >
                      <span class="i-lucide-user size-3" />
                      {{ log.contact_name }}
                    </span>
                    <span
                      v-if="log.phone_number"
                      class="flex items-center gap-1"
                    >
                      <span class="i-lucide-phone size-3" />
                      {{ log.phone_number }}
                    </span>
                  </div>
                </div>
                <button
                  v-if="log.record_data"
                  class="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-n-blue-11 hover:bg-n-blue-3 transition-colors"
                  @click="toggleRecordData(log.id)"
                >
                  <span
                    class="size-3.5"
                    :class="
                      expandedLogs.has(log.id)
                        ? 'i-lucide-chevron-up'
                        : 'i-lucide-braces'
                    "
                  />
                  {{
                    expandedLogs.has(log.id)
                      ? $t('CAMPAIGN.STATISTICS.HIDE_RECORD_DATA')
                      : $t('CAMPAIGN.STATISTICS.SHOW_RECORD_DATA')
                  }}
                </button>
              </div>
              <div
                v-if="expandedLogs.has(log.id)"
                class="bg-n-solid-2 p-3 rounded-md border border-n-slate-6 overflow-x-auto"
              >
                <VueJsonPretty
                  :data="log.record_data || {}"
                  :deep="3"
                  show-double-quotes
                  show-length
                  :show-line="false"
                  show-icon
                />
              </div>
            </div>
          </div>

          <!-- Empty: campanha falhou mas sem logs detalhados (rescue do job) -->
          <div
            v-else-if="isFailedStatus && statusMessage"
            class="flex flex-1 flex-col items-center justify-center text-n-ruby-11"
          >
            <span class="i-lucide-alert-octagon size-8 mb-2" />
            <span class="text-sm font-medium mb-1">{{
              $t('CAMPAIGN.STATISTICS.EXECUTION_FAILED_TITLE')
            }}</span>
            <span class="text-xs text-n-slate-11 text-center max-w-md px-4">
              {{ statusMessage }}
            </span>
          </div>
          <!-- Empty: sem erros -->
          <div
            v-else
            class="flex flex-1 flex-col items-center justify-center text-n-slate-11"
          >
            <span class="i-lucide-check-circle size-8 mb-2" />
            <span class="text-sm">{{
              $t('CAMPAIGN.STATISTICS.NO_ERRORS_FOUND')
            }}</span>
          </div>

          <!-- Pagination -->
          <div
            v-if="totalPages > 1"
            class="flex items-center justify-between pt-4 mt-4 border-t border-n-slate-6 shrink-0"
          >
            <button
              :disabled="currentPage === 1"
              class="flex items-center gap-1 text-sm text-n-slate-11 hover:text-n-slate-12 disabled:opacity-50"
              @click="prevPage"
            >
              <span class="i-lucide-chevron-left size-4" />
              {{ $t('CAMPAIGN.STATISTICS.PREVIOUS') }}
            </button>
            <span class="text-xs text-n-slate-10">
              {{ currentPage }} / {{ totalPages }}
            </span>
            <button
              :disabled="currentPage === totalPages"
              class="flex items-center gap-1 text-sm text-n-slate-11 hover:text-n-slate-12 disabled:opacity-50"
              @click="nextPage"
            >
              {{ $t('CAMPAIGN.STATISTICS.NEXT') }}
              <span class="i-lucide-chevron-right size-4" />
            </button>
          </div>
        </div>
        <!-- /drawer -->
      </Transition>
    </div>
    <!-- /container drawer -->
  </div>
</template>
